/**
 * demoAccess.js — Operational Sandbox access-control client.
 * ---------------------------------------------------------
 * Reads `?token=XXXX` from the URL once on app load, validates it
 * server-side via /api/demo/validate-token, and persists the resulting
 * session + access metadata in localStorage so subsequent navigation
 * doesn't re-burn a session.
 *
 * Access modes:
 *   public_preview — no token (default). Form submission gated.
 *   sandbox        — token validated, full forms + ecosystem.
 *   qualified      — token validated, sandbox + white-label depth.
 *
 * Three exports:
 *   useDemoAccess()  — React hook returning the current access object
 *   trackEvent(kind, payload)
 *   canPerform(action) — boolean gate for action strings
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API_BASE   = process.env.REACT_APP_BACKEND_URL || "";
const ACCESS_KEY = "forgeops.demo.access";

const DEFAULT_ACCESS = {
  valid:         false,
  access_level:  "public_preview",
  session_id:    null,
  lead_id:       null,
  prospect_name: null,
  company:       null,
  tenant_id:     null,
  allowed_roles: [],
  expires_at:    null,
  // Soft mode-aware capability map. Real source of truth is server's
  // /api/demo/access-modes endpoint; the hook fetches it on mount and
  // overwrites this. Keeping a sane default here for first paint.
  allow: ["landing", "auto_tour", "module_preview", "language_toggle"],
};


function _readAccess() {
  try {
    const raw = window.localStorage.getItem(ACCESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch { /* */ }
  return null;
}

function _writeAccess(obj) {
  try { window.localStorage.setItem(ACCESS_KEY, JSON.stringify(obj)); } catch { /* */ }
}

function _clearAccess() {
  try { window.localStorage.removeItem(ACCESS_KEY); } catch { /* */ }
}


// ──────────────────────── Event tracking ──────────────────────────
// Always best-effort — never blocks the UI thread, never throws.
export async function trackEvent(kind, payload = {}) {
  try {
    const access = _readAccess() || {};
    await fetch(`${API_BASE}/api/demo/track-event`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: access.session_id || null,
        kind,
        payload,
      }),
      keepalive: true,
    });
  } catch { /* fire-and-forget */ }
}


// ───────────────────── Capability gate ────────────────────────
// `action` strings match what /api/demo/access-modes returns.
export function canPerform(access, action) {
  if (!access) return false;
  const allowed = access.allow || [];
  return allowed.includes(action);
}


// ───────────────────── Token validation flow ─────────────────────
async function validateToken(token) {
  try {
    const r = await fetch(`${API_BASE}/api/demo/validate-token`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      return { ok: false, status: r.status, error: err.detail || err };
    }
    const body = await r.json();
    return { ok: true, body };
  } catch (e) {
    return { ok: false, status: 0, error: { reason: "network" } };
  }
}


async function loadAccessModes() {
  try {
    const r = await fetch(`${API_BASE}/api/demo/access-modes`);
    if (r.ok) return await r.json();
  } catch { /* */ }
  return null;
}


// ─────────────────────── React context ─────────────────────────
const DemoAccessContext = createContext(DEFAULT_ACCESS);

export function DemoAccessProvider({ children }) {
  const [access, setAccess] = useState(() => _readAccess() || DEFAULT_ACCESS);
  const [error,  setError]  = useState(null);   // {reason, message} on bad token

  // Boot: check URL for ?token=, fetch access modes, validate if needed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get("token");
      const modes = await loadAccessModes();

      if (urlToken) {
        // New token in URL — validate it server-side.
        const result = await validateToken(urlToken);
        if (cancelled) return;
        if (result.ok) {
          const merged = {
            ...result.body,
            valid: true,
            allow: modes?.[result.body.access_level]?.allow || [],
          };
          _writeAccess(merged);
          setAccess(merged);
          // Strip the token from the URL so a copy-paste doesn't leak it.
          const url = new URL(window.location.href);
          url.searchParams.delete("token");
          window.history.replaceState({}, "", url.toString());
        } else {
          _clearAccess();
          setError(result.error);
          // Public-preview is still served — error is exposed to caller
          // for the AccessExpired page to consume if it routes there.
          setAccess({
            ...DEFAULT_ACCESS,
            allow: modes?.public_preview?.allow || DEFAULT_ACCESS.allow,
          });
        }
        return;
      }

      // No new token — refresh the modes-derived `allow` array on the
      // existing access doc so a code update to the modes table takes
      // effect on next page load.
      const cur = _readAccess() || DEFAULT_ACCESS;
      const lvl = cur.access_level || "public_preview";
      const merged = { ...cur, allow: modes?.[lvl]?.allow || cur.allow || [] };
      _writeAccess(merged);
      setAccess(merged);

      // Light page-view tracking on every navigation.
      if (cur.session_id) {
        trackEvent("page_view", { path: window.location.pathname });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signOut = useCallback(() => {
    _clearAccess();
    setAccess(DEFAULT_ACCESS);
  }, []);

  return (
    <DemoAccessContext.Provider value={{ access, error, signOut }}>
      {children}
    </DemoAccessContext.Provider>
  );
}

export function useDemoAccess() {
  return useContext(DemoAccessContext);
}
